/**
 * Typed client for the pages API. All URLs are relative — Vite proxies them in development,
 * FastAPI serves same-origin in production.
 */

export type Space = { id: string; slug: string; name: string; kind: "shared" | "private" };

export type TreePage = { id: string; title: string; parent_id: string | null; position: number };

export type EditionInfo = {
  edition: number;
  revision: number;
  approved_by: string | null;
  approved_at: string;
  note: string | null;
  superseded: boolean;
};

export type EditorEntry = { id: string; display_name: string };

export type ProtectionInfo = {
  protected: boolean;
  own: boolean;
  inherited_from_id: string | null;
  inherited_from_title: string | null;
  editors: EditorEntry[];
  can_edit: boolean;
  can_manage: boolean;
};

export type SensitivityInfo = {
  sensitive: boolean;
  own: boolean;
  inherited_from_id: string | null;
  inherited_from_title: string | null;
  can_manage: boolean;
};

export type PageDetail = {
  id: string;
  space_id: string;
  parent_id: string | null;
  title: string;
  slug: string;
  content: Record<string, unknown>;
  text_cache: string;
  current_revision: number;
  updated_at: string;
  review_due_at: string | null;
  latest_edition: EditionInfo | null;
  protection: ProtectionInfo | null;
  sensitivity: SensitivityInfo | null;
};

export type ReviewDue = {
  page_id: string;
  title: string;
  space_name: string;
  review_due_at: string;
  overdue: boolean;
};

export type RevisionListItem = {
  revision: number;
  title: string;
  author: string | null;
  summary: string | null;
  created_at: string;
};

export type RevisionDetail = {
  revision: number;
  title: string;
  text_cache: string;
  created_at: string;
};

export type Backlink = { page_id: string; title: string };

export type Suggestion = { kind: "page" | "entity"; id: string; title: string; hint: string | null };

export type AdminSpace = {
  id: string;
  slug: string;
  name: string;
  kind: "shared" | "private";
  owner_id: string | null;
  owner_name: string | null;
  page_count: number;
};

export type AdminUserInfo = {
  id: string;
  email: string;
  display_name: string;
  role: "admin" | "member";
  is_active: boolean;
  has_entra_identity: boolean;
  created_at: string;
};

export type SystemInfo = {
  version: string;
  environment: string;
  auth_mode: string;
  model_router_mode: string;
  migration_revision: string | null;
  users: number;
  spaces: number;
  pages: number;
  captures: number;
};

export type AiProfile = {
  id: string;
  name: string;
  kind: "local" | "cloud";
  /** Where the data lands. Enforced by the router's gate, not inferred from the URL (2026-08-20). */
  jurisdiction: "house" | "eu" | "outside_eu";
  base_url: string;
  model: string;
  api_key_env: string;
  key_present: boolean;
  params: Record<string, unknown>;
};

export type AiUsageRow = {
  task_class: string;
  calls: number;
  tokens_in: number;
  tokens_out: number;
  cost: number;
};

/** The semantic index as the admin sees it (WP1.2). */
export type AiIndexStatus = {
  pages_indexed: number;
  pages_pending: number;
  chunks: number;
  chunks_embedded: number;
  /** More than one row means the index spans two vector spaces and half of it is unreachable. */
  models: { model: string; chunks: number }[];
};

export type AiIndexRun = {
  pages: number;
  chunks_written: number;
  chunks_removed: number;
  embedded: number;
  remaining: number;
  model: string | null;
  note: string | null;
};

/** The acceptance queue (WP1.5). */
export type QueueSource = {
  quote: string;
  capture_id: string | null;
  page_id: string | null;
  anchor: string | null;
  char_start: number | null;
  char_end: number | null;
  offset_ms: number | null;
  external_id: string | null;
};

export type QueueProposal = {
  id: string;
  kind: "decision" | "status_update" | "assumption" | "promise" | "commitment";
  space_id: string;
  space_name: string;
  text: string;
  why: string | null;
  /** A status update proposes a rewrite; this is the terse line it replaces. */
  original: string | null;
  project_id: string | null;
  project_name: string | null;
  entities: { id: string; name: string; kind: string }[];
  unknown_entities: string[];
  confidence: number | null;
  prompt_version: string | null;
  created_at: string;
  sources: QueueSource[];
};

export type QueueBundle = {
  project_id: string | null;
  project_name: string | null;
  proposals: QueueProposal[];
};

export type ReviewQueue = { bundles: QueueBundle[]; total: number };

export type DecisionInfo = {
  id: string;
  space_id: string;
  project_id: string | null;
  text: string;
  why: string;
  supersedes_id: string | null;
  decided_at: string;
  decided_by: string | null;
  sources: QueueSource[];
};

export type AcceptResult = {
  proposal_id: string;
  status: string;
  decision: DecisionInfo | null;
};

/** The pipeline ledger as the admin sees it (WP1.3). */
export type PipelineStats = {
  pending: number;
  running: number;
  succeeded: number;
  failed: number;
  dead: number;
  discarded: number;
  proposals_pending: number;
};

export type PipelineDeadLetter = {
  id: string;
  kind: string;
  source_type: string;
  source_id: string;
  attempts: number;
  max_attempts: number;
  last_error: string | null;
  request_id: string | null;
  updated_at: string;
};

export type PipelineOverview = {
  stats: PipelineStats;
  dead_letters: PipelineDeadLetter[];
  /** Job kinds that actually have a handler. A kind without one can never finish. */
  handlers: string[];
};

export type PipelineRun = {
  claimed: number;
  succeeded: number;
  failed: number;
  proposals: number;
  errors: string[];
};

export type AiOverview = {
  mode: string;
  ai_enabled: boolean;
  cloud_enabled: boolean;
  /** The narrower switch: cloud may be on while data still may not leave the EU. */
  allow_outside_eu: boolean;
  profiles: AiProfile[];
  assignments: Record<string, string | null>;
  usage: AiUsageRow[];
  index: AiIndexStatus;
};

export type AiTestResult = {
  status: "ok" | "refused" | "error";
  message: string;
  profile_name: string | null;
  model: string | null;
  latency_ms: number | null;
  fallback: boolean;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: Record<string, unknown>,
  ) {
    super(String(body.detail ?? `HTTP ${status}`));
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    ...init,
  });
  if (!response.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      // A body-less error keeps its status code.
    }
    throw new ApiError(response.status, body);
  }
  return (await response.json()) as T;
}

export type AttachmentInfo = {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  url: string;
  thumbnail_url: string | null;
};

export async function uploadAttachment(spaceId: string, file: File): Promise<AttachmentInfo> {
  const form = new FormData();
  form.append("file", file, file.name || "urklipp");
  form.append("space_id", spaceId);
  const response = await fetch("/api/v1/attachments", { method: "POST", body: form });
  if (!response.ok) {
    let body: Record<string, unknown> = {};
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      // Keep the status code.
    }
    throw new ApiError(response.status, body);
  }
  return (await response.json()) as AttachmentInfo;
}

export type CaptureInfo = {
  id: string;
  space_id: string;
  body: string;
  author: string | null;
  created_at: string;
  archived_at: string | null;
  archived_to_page_id: string | null;
  archived_to_page_title: string | null;
  attachments: AttachmentInfo[];
};

export type CapturePage = { items: CaptureInfo[]; next_cursor: string | null };

export type ProjectStatus = "active" | "paused" | "closed";

export type ProjectListItem = {
  id: string;
  name: string;
  status: ProjectStatus;
  status_note: string;
  page_id: string | null;
  open_checklist_items: number;
  updated_at: string;
};

export type ProjectItemKind = "promise" | "commitment" | "assumption";

export type ProjectItemInfo = {
  id: string;
  kind: ProjectItemKind;
  body: string;
  author: string | null;
  created_at: string;
};

export type ChecklistItemInfo = {
  id: string;
  text: string;
  done: boolean;
  done_by: string | null;
};

export type ChecklistInfo = { id: string; name: string; items: ChecklistItemInfo[] };

export type ProjectDetail = {
  id: string;
  name: string;
  slug: string;
  status: ProjectStatus;
  status_note: string;
  entity_id: string;
  page_id: string | null;
  items: ProjectItemInfo[];
  checklists: ChecklistInfo[];
  updated_at: string;
};

export type ChecklistTemplateInfo = { id: string; name: string; items: string[] };

export type FavoriteKind = "page" | "project";

export type FavoriteInfo = {
  kind: FavoriteKind;
  target_id: string;
  title: string;
  hint: string | null;
};

export type ActivityInfo = {
  verb: string;
  title: string;
  kind: string;
  target_id: string;
  occurred_at: string;
};

export type SearchResult = {
  page_id: string;
  /** Carries U+E000/U+E001 around each hit; render with segmentHits(). */
  title: string;
  snippet: string;
  space_id: string;
  space_name: string;
  updated_at: string;
  /** Which leg found the page (WP1.2): the words, the meaning, or both. */
  sources: ("text" | "semantic")[];
  /** The chunk anchor behind a semantic hit — the stable pointer citations reuse. */
  anchor: string | null;
};

export type SearchFacet = { entity_id: string; name: string; kind: string; count: number };

/** What the semantic index could offer this user, so the UI never promises more (WP1.2). */
export type SemanticState = {
  ready: boolean;
  model: string | null;
  chunks: number;
  unavailable_reason: string | null;
};

export type SearchResponse = {
  results: SearchResult[];
  facets: SearchFacet[];
  total: number;
  /** The mode actually used — "text" when hybrid was asked for but could not answer. */
  mode: "text" | "hybrid";
  semantic: SemanticState;
};

/**
 * Split server-marked text into segments for safe rendering: the server marks hits with two
 * private-use-area characters instead of HTML, so page content can never smuggle markup here.
 */
export function segmentHits(marked: string): { text: string; hit: boolean }[] {
  return marked
    .split("")
    .flatMap((part, index) => {
      if (index === 0) {
        return [{ text: part, hit: false }];
      }
      const [hit, ...rest] = part.split("");
      return [
        { text: hit, hit: true },
        { text: rest.join(""), hit: false },
      ];
    })
    .filter((segment) => segment.text !== "");
}

export const api = {
  spaces: () => request<Space[]>("/api/v1/spaces"),
  pageTree: (spaceId: string) => request<TreePage[]>(`/api/v1/spaces/${spaceId}/pages`),
  page: (id: string) => request<PageDetail>(`/api/v1/pages/${id}`),
  createPage: (body: {
    space_id: string;
    title: string;
    parent_id?: string | null;
    template_id?: string | null;
  }) => request<PageDetail>("/api/v1/pages", { method: "POST", body: JSON.stringify(body) }),
  restorePage: (id: string) =>
    request<{ restored: number }>(`/api/v1/pages/${id}/restore`, { method: "POST", body: "{}" }),
  archivedPages: (spaceId: string) =>
    request<{ id: string; title: string; deleted_at: string }[]>(
      `/api/v1/spaces/${spaceId}/pages/archived`,
    ),
  pageTemplates: () => request<{ id: string; name: string }[]>("/api/v1/page-templates"),
  createPageTemplate: (body: { name: string; page_id: string }) =>
    request<{ id: string; name: string }>("/api/v1/page-templates", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removePageTemplate: (id: string) =>
    request<{ removed: boolean }>(`/api/v1/page-templates/${id}`, { method: "DELETE" }),
  savePage: (
    id: string,
    body: { base_revision: number; title?: string; content?: Record<string, unknown> },
  ) => request<PageDetail>(`/api/v1/pages/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  movePage: (id: string, body: { parent_id: string | null; position: number }) =>
    request<PageDetail>(`/api/v1/pages/${id}/move`, { method: "PATCH", body: JSON.stringify(body) }),
  archivePage: (id: string) =>
    request<{ archived: number }>(`/api/v1/pages/${id}`, { method: "DELETE" }),
  revisions: (id: string) => request<RevisionListItem[]>(`/api/v1/pages/${id}/revisions`),
  revision: (id: string, revision: number) =>
    request<RevisionDetail>(`/api/v1/pages/${id}/revisions/${revision}`),
  backlinks: (id: string) => request<Backlink[]>(`/api/v1/pages/${id}/backlinks`),
  suggest: (q: string, spaceId?: string) =>
    request<Suggestion[]>(
      `/api/v1/links/suggest?q=${encodeURIComponent(q)}${spaceId ? `&space_id=${spaceId}` : ""}`,
    ),

  // --- the stream (WP0.5) ---
  createCapture: (body: { space_id: string; body: string; attachment_ids?: string[] }) =>
    request<CaptureInfo>("/api/v1/captures", { method: "POST", body: JSON.stringify(body) }),
  captures: (params: {
    spaceId?: string;
    includeArchived?: boolean;
    before?: string | null;
    limit?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.spaceId) query.set("space_id", params.spaceId);
    if (params.includeArchived) query.set("include_archived", "true");
    if (params.before) query.set("before", params.before);
    if (params.limit) query.set("limit", String(params.limit));
    return request<CapturePage>(`/api/v1/captures?${query.toString()}`);
  },
  archiveCapture: (id: string, pageId: string) =>
    request<CaptureInfo>(`/api/v1/captures/${id}/archive`, {
      method: "POST",
      body: JSON.stringify({ page_id: pageId }),
    }),
  unarchiveCapture: (id: string) =>
    request<CaptureInfo>(`/api/v1/captures/${id}/unarchive`, { method: "POST", body: "{}" }),

  // --- project rooms (WP0.5) ---
  projects: () => request<ProjectListItem[]>("/api/v1/projects"),
  project: (id: string) => request<ProjectDetail>(`/api/v1/projects/${id}`),
  createProject: (body: { name: string; space_id: string }) =>
    request<ProjectDetail>("/api/v1/projects", { method: "POST", body: JSON.stringify(body) }),
  patchProject: (
    id: string,
    body: { name?: string; status?: ProjectStatus; status_note?: string },
  ) => request<ProjectDetail>(`/api/v1/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  addProjectItem: (projectId: string, body: { kind: ProjectItemKind; body: string }) =>
    request<ProjectItemInfo>(`/api/v1/projects/${projectId}/items`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateProjectItem: (itemId: string, body: string) =>
    request<ProjectItemInfo>(`/api/v1/project-items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    }),
  removeProjectItem: (itemId: string) =>
    request<{ removed: boolean }>(`/api/v1/project-items/${itemId}`, { method: "DELETE" }),
  checklistTemplates: () => request<ChecklistTemplateInfo[]>("/api/v1/checklist-templates"),
  createChecklistTemplate: (body: { name: string; items: string[] }) =>
    request<ChecklistTemplateInfo>("/api/v1/checklist-templates", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeChecklistTemplate: (id: string) =>
    request<{ removed: boolean }>(`/api/v1/checklist-templates/${id}`, { method: "DELETE" }),
  createChecklist: (projectId: string, body: { template_id?: string; name?: string }) =>
    request<ChecklistInfo>(`/api/v1/projects/${projectId}/checklists`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeChecklist: (id: string) =>
    request<{ removed: boolean }>(`/api/v1/checklists/${id}`, { method: "DELETE" }),
  toggleChecklistItem: (id: string, done: boolean) =>
    request<ChecklistItemInfo>(`/api/v1/checklist-items/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ done }),
    }),

  // --- favourites and own activity ---
  favorites: () => request<FavoriteInfo[]>("/api/v1/me/favorites"),
  addFavorite: (kind: FavoriteKind, targetId: string) =>
    request<FavoriteInfo[]>("/api/v1/me/favorites", {
      method: "PUT",
      body: JSON.stringify({ kind, target_id: targetId }),
    }),
  removeFavorite: (kind: FavoriteKind, targetId: string) =>
    request<FavoriteInfo[]>(`/api/v1/me/favorites/${kind}/${targetId}`, { method: "DELETE" }),
  orderFavorites: (order: { kind: FavoriteKind; target_id: string }[]) =>
    request<FavoriteInfo[]>("/api/v1/me/favorites/order", {
      method: "PATCH",
      body: JSON.stringify({ order }),
    }),
  myActivity: () => request<ActivityInfo[]>("/api/v1/me/activity"),
  reviewDue: () => request<ReviewDue[]>("/api/v1/me/review-due"),

  // --- governed documents (WP0.9) ---
  fixEdition: (pageId: string, note?: string) =>
    request<EditionInfo>(`/api/v1/pages/${pageId}/editions`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  setReview: (pageId: string, reviewDueAt: string | null) =>
    request<PageDetail>(`/api/v1/pages/${pageId}/review`, {
      method: "PUT",
      body: JSON.stringify({ review_due_at: reviewDueAt }),
    }),
  projectGallery: (projectId: string) =>
    request<AttachmentInfo[]>(`/api/v1/projects/${projectId}/gallery`),

  // --- protected pages (WP0.8) ---
  users: () => request<EditorEntry[]>("/api/v1/users"),
  setProtection: (pageId: string, body: { protected: boolean; editor_ids: string[] }) =>
    request<ProtectionInfo>(`/api/v1/pages/${pageId}/protection`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // --- admin portal (WP0.11) ---
  adminSpaces: () => request<AdminSpace[]>("/api/v1/admin/spaces"),
  adminCreateSpace: (body: { name: string; kind: "shared" | "private"; owner_id?: string | null }) =>
    request<AdminSpace>("/api/v1/admin/spaces", { method: "POST", body: JSON.stringify(body) }),
  adminRenameSpace: (id: string, name: string) =>
    request<AdminSpace>(`/api/v1/admin/spaces/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  adminUsers: () => request<AdminUserInfo[]>("/api/v1/admin/users"),
  adminSetUserActive: (id: string, isActive: boolean) =>
    request<AdminUserInfo>(`/api/v1/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ is_active: isActive }),
    }),
  adminSystem: () => request<SystemInfo>("/api/v1/admin/system"),

  // --- AI (WP1.1) ---
  aiOverview: () => request<AiOverview>("/api/v1/admin/ai"),
  aiCreateProfile: (body: {
    name: string;
    kind: "local" | "cloud";
    jurisdiction?: "house" | "eu" | "outside_eu";
    base_url: string;
    model: string;
    api_key_env?: string;
    params?: Record<string, unknown>;
  }) => request<AiProfile>("/api/v1/admin/ai/profiles", { method: "POST", body: JSON.stringify(body) }),
  aiUpdateProfile: (
    id: string,
    body: Partial<{
      name: string;
      kind: "local" | "cloud";
      jurisdiction: "house" | "eu" | "outside_eu";
      base_url: string;
      model: string;
      api_key_env: string;
      params: Record<string, unknown>;
    }>,
  ) => request<AiProfile>(`/api/v1/admin/ai/profiles/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  aiRemoveProfile: (id: string) =>
    request<{ removed: boolean }>(`/api/v1/admin/ai/profiles/${id}`, { method: "DELETE" }),
  aiAssign: (taskClass: string, profileId: string | null) =>
    request<Record<string, string | null>>(`/api/v1/admin/ai/assignments/${taskClass}`, {
      method: "PUT",
      body: JSON.stringify({ profile_id: profileId }),
    }),
  aiSwitches: (body: {
    ai_enabled?: boolean;
    cloud_enabled?: boolean;
    allow_outside_eu?: boolean;
  }) =>
    request<{ ai_enabled: boolean; cloud_enabled: boolean; allow_outside_eu: boolean }>(
      "/api/v1/admin/ai/settings",
      { method: "PUT", body: JSON.stringify(body) },
    ),
  aiTest: (taskClass: string) =>
    request<AiTestResult>(`/api/v1/admin/ai/test/${taskClass}`, { method: "POST", body: "{}" }),
  setSensitivity: (pageId: string, sensitive: boolean) =>
    request<SensitivityInfo>(`/api/v1/pages/${pageId}/sensitivity`, {
      method: "PUT",
      body: JSON.stringify({ sensitive }),
    }),

  // --- search (WP0.6, hybrid in WP1.2) ---
  search: (params: {
    q: string;
    spaceId?: string;
    entityId?: string;
    mode?: "text" | "hybrid";
  }) => {
    const query = new URLSearchParams({ q: params.q });
    if (params.spaceId) query.set("space_id", params.spaceId);
    if (params.entityId) query.set("entity_id", params.entityId);
    if (params.mode) query.set("mode", params.mode);
    return request<SearchResponse>(`/api/v1/search?${query.toString()}`);
  },

  // --- the semantic index (WP1.2) ---
  aiIndexStatus: () => request<AiIndexStatus>("/api/v1/admin/ai/index"),

  aiIndexRun: () => request<AiIndexRun>("/api/v1/admin/ai/index/run", { method: "POST", body: "{}" }),

  // --- the acceptance queue (WP1.5) ---
  reviewQueue: (projectId?: string) => {
    const query = projectId ? `?project_id=${projectId}` : "";
    return request<ReviewQueue>(`/api/v1/review/queue${query}`);
  },

  acceptProposal: (
    proposalId: string,
    body: {
      text?: string;
      why?: string;
      project_id?: string;
      clear_project?: boolean;
      supersedes_id?: string;
    },
  ) =>
    request<AcceptResult>(`/api/v1/proposals/${proposalId}/accept`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  rejectProposal: (proposalId: string) =>
    request<AcceptResult>(`/api/v1/proposals/${proposalId}/reject`, {
      method: "POST",
      body: "{}",
    }),

  decisions: (projectId?: string) =>
    request<DecisionInfo[]>(
      projectId ? `/api/v1/projects/${projectId}/decisions` : "/api/v1/decisions",
    ),

  decisionChain: (decisionId: string) =>
    request<DecisionInfo[]>(`/api/v1/decisions/${decisionId}/chain`),

  // --- the pipeline (WP1.3) ---
  pipelineOverview: () => request<PipelineOverview>("/api/v1/admin/pipeline"),

  pipelineRun: () =>
    request<PipelineRun>("/api/v1/admin/pipeline/run", { method: "POST", body: "{}" }),

  pipelineRetry: (jobId: string) =>
    request<PipelineDeadLetter>(`/api/v1/admin/pipeline/jobs/${jobId}/retry`, {
      method: "POST",
      body: "{}",
    }),

  pipelineDiscard: (jobId: string) =>
    request<PipelineDeadLetter>(`/api/v1/admin/pipeline/jobs/${jobId}/discard`, {
      method: "POST",
      body: "{}",
    }),

  aiIndexRebuild: (rechunk: boolean) =>
    request<{ embeddings_dropped: number; pages_marked: number }>("/api/v1/admin/ai/index/rebuild", {
      method: "POST",
      body: JSON.stringify({ rechunk }),
    }),
};
